import { useEffect, useState, type FormEvent } from 'react';
import { segment } from '@actions/analytics';
import { REFERRAL_PROMOTION_ADD_BUDDY } from './segment';
import {
  AvatarImage,
  avatarImage,
  campaignImage,
  Eyebrow,
  IndiaFlag,
  SectionTitle
} from './shared';
import { fetchReferralSquad, inviteReferralBuddy } from './api';
import {
  AddButton,
  BuilderGrid,
  BuilderHeader,
  BuilderIntro,
  BuilderPanel,
  Divider,
  EmptyCard,
  EmptyHintCard,
  EmptyHintText,
  EmptyInvite,
  EmptyPanel,
  EmptyRow,
  Field,
  FilledHint,
  FormRow,
  Hint,
  InputShell,
  InviteCard,
  Member,
  MemberGrid,
  MoreAvatar,
  PhoneInput,
  Prefix,
  SquadCard,
  SquadLabel,
  Stack,
  StackAvatar
} from './squadBuilderStyles';
import type { ReferralSquadData, SquadMember } from './types';

type SquadBuilderProps = {
  members: SquadMember[];
  extraCount: number;
  promotionPart: 2 | 3;
  onSquadUpdate?: (data: ReferralSquadData) => void;
};

const SquadBuilder = ({
  members,
  extraCount,
  promotionPart,
  onSquadUpdate
}: SquadBuilderProps) => {
  const [phone, setPhone] = useState('');
  const [localMembers, setLocalMembers] = useState(members);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault();
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length < 10 || isSubmitting) return;

    const alreadyAdded = localMembers.some((member) => member.phone === digits);
    if (alreadyAdded) {
      setPhone('');
      return;
    }

    segment.track(REFERRAL_PROMOTION_ADD_BUDDY, {
      promotion_part: promotionPart
    });

    setIsSubmitting(true);
    const invite = await inviteReferralBuddy({ phone: digits, name: '' });

    if (!invite) {
      setIsSubmitting(false);
      return;
    }

    const squadData = await fetchReferralSquad();
    setLocalMembers(squadData.members);
    onSquadUpdate?.(squadData);
    setIsSubmitting(false);
    setPhone('');
  };

  const visibleMembers = localMembers.slice(0, 9);
  const overflowCount = Math.max(extraCount, localMembers.length - 9);
  const hasSquad = visibleMembers.length > 0 || overflowCount > 0;

  const inviteForm = (flush?: boolean) => (
    <FormRow $flush={flush} onSubmit={handleAdd}>
      <Field>
        Your friend&apos;s mobile number
        <InputShell>
          <IndiaFlag />
          <Prefix>+91</Prefix>
          <Divider />
          <PhoneInput
            inputMode="numeric"
            maxLength={10}
            value={phone}
            onChange={(event) =>
              setPhone(event.target.value.replace(/\D/g, '').slice(0, 10))
            }
            placeholder="Enter mobile number"
          />
        </InputShell>
      </Field>
      <AddButton
        type="submit"
        $wide={flush}
        disabled={isSubmitting || phone.replace(/\D/g, '').length < 10}
      >
        {isSubmitting ? 'ADDING...' : 'ADD BUDDY'}
      </AddButton>
    </FormRow>
  );

  const avatarStack = (
    <Stack>
      <StackAvatar src={campaignImage('stack-avatar-1.svg')} alt="" />
      <StackAvatar src={campaignImage('stack-avatar-2.svg')} alt="" />
      <StackAvatar src={campaignImage('stack-avatar-3.svg')} alt="" />
    </Stack>
  );

  if (!hasSquad) {
    return (
      <EmptyPanel>
        <BuilderHeader>
          <BuilderIntro>
            <Eyebrow>
              <img
                src={campaignImage('poll-icon.svg')}
                alt=""
                width={24}
                height={24}
              />
              Add your buddy
            </Eyebrow>
            <SectionTitle>You can still add more friends</SectionTitle>
          </BuilderIntro>
          <EmptyHintCard>
            <EmptyHintText>
              Add your friends and create your squad for great winnings in
              future
            </EmptyHintText>
          </EmptyHintCard>
        </BuilderHeader>
        <EmptyCard>
          <EmptyRow>
            <EmptyInvite>
              {avatarStack}
              <Hint>Add you friends below</Hint>
            </EmptyInvite>
            {inviteForm(true)}
          </EmptyRow>
        </EmptyCard>
      </EmptyPanel>
    );
  }

  return (
    <BuilderPanel>
      <BuilderHeader>
        <BuilderIntro>
          <Eyebrow>
            <img
              src={campaignImage('poll-icon.svg')}
              alt=""
              width={24}
              height={24}
            />
            Add your buddy
          </Eyebrow>
          <SectionTitle>Create your squad</SectionTitle>
        </BuilderIntro>
      </BuilderHeader>
      <BuilderGrid>
        <InviteCard>
          {avatarStack}
          <FilledHint>Add you friends below</FilledHint>
          {inviteForm()}
        </InviteCard>
        <SquadCard>
          <SquadLabel>
            <img
              src={campaignImage('users.svg')}
              alt=""
              width={18}
              height={18}
            />
            Your Squad
          </SquadLabel>
          <MemberGrid>
            {visibleMembers.map((member) => (
              <Member key={member.id}>
                <AvatarImage src={avatarImage(member.avatarIndex)} alt="" />
                {member.label}
              </Member>
            ))}
            {overflowCount > 0 ? (
              <Member>
                <MoreAvatar>
                  <img src={campaignImage('plus.svg')} alt="" />
                </MoreAvatar>
                +{overflowCount} more
              </Member>
            ) : null}
          </MemberGrid>
        </SquadCard>
      </BuilderGrid>
    </BuilderPanel>
  );
};

export default SquadBuilder;
